import type { FormEvent, Ref } from 'react'
import type { Draft } from '../guests'
import { coverageSentence } from '../guests'
import type { Invite, RsvpResponse } from '../types'
import { weddingInfo } from '../weddingInfo'
import { PersonChoice } from './PersonChoice'

/** What the card is doing during the send-off. See useLetterDelivery. */
export type CardMotion =
  | 'none'
  /** Sealed: the answers are in and can no longer be changed. */
  | 'locking'
  /** The dog has just barged it. */
  | 'shaking'
  /** Shrinking down into the letter. */
  | 'shrinking'
  /** Gone with the dog. The box stays, so the page below does not jump. */
  | 'gone'

const MOTION_CLASS: Record<CardMotion, string> = {
  none: '',
  locking: 'animate-card-lock',
  shaking: 'animate-card-shake',
  shrinking: 'animate-card-shrink',
  // `invisible` rather than `hidden`: the card keeps its box while the dog
  // carries it away, which is what stops the page below snapping upwards.
  gone: 'invisible',
}

interface RsvpFormProps {
  /** The card element itself, which RsvpCard measures and animates. */
  ref?: Ref<HTMLDivElement> | undefined
  invite: Invite
  draft: Draft
  busy: boolean
  answeredAll: boolean
  error: string | null
  motion: CardMotion
  onChange: (personId: string, response: RsvpResponse) => void
  onSubmit: (event: FormEvent) => void
}

/**
 * The RSVP form itself - markup only. Every piece of state it renders belongs
 * to RsvpCard, which also owns the submit sequence.
 */
export function RsvpForm({
  ref,
  invite,
  draft,
  busy,
  answeredAll,
  error,
  motion,
  onChange,
  onSubmit,
}: RsvpFormProps) {
  const sealed = motion !== 'none'

  return (
    <div
      ref={ref}
      // Once sealed, nothing inside can be reached by tab or click either, so
      // the lock is real and not only visual.
      {...(sealed ? { inert: true } : {})}
      className={[
        'border-ink/10 bg-parchment/70 rounded-lg border p-6 shadow-sm sm:p-8',
        MOTION_CLASS[motion],
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <h2 className="font-display text-2xl">Will you join us?</h2>
      <p className="mt-1 text-sm">{coverageSentence(invite.people)}</p>
      <p className="text-ink-soft mt-1 text-sm">
        Please reply by {weddingInfo.rsvpBy}. You can change your answer any time before then.
      </p>
      {/* The ask, one line under the date rather than woven into it - the date is
          a deadline and this is a favour, and running them together would soften
          the first into the second. */}
      <p className="text-claret mt-1 text-sm">{weddingInfo.rsvpEarly}</p>

      <form onSubmit={onSubmit} className="mt-6 space-y-5">
        {invite.people.map((person) => (
          <PersonChoice
            key={person.person_id}
            name={person.name}
            inviteType={person.invite_type}
            value={draft[person.person_id] ?? 'waiting'}
            disabled={busy}
            onChange={(response) => onChange(person.person_id, response)}
          />
        ))}

        {error && (
          <p role="alert" className="text-claret text-sm">
            {error}
          </p>
        )}

        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          <button
            type="submit"
            disabled={busy || !answeredAll}
            className="bg-claret hover:bg-claret-dark rounded-md px-6 py-3 text-sm tracking-widest text-white uppercase transition-colors disabled:cursor-not-allowed disabled:opacity-50"
          >
            {busy ? 'Sending…' : 'Send our reply'}
          </button>
          {!answeredAll && (
            <p className="text-ink-soft text-xs">Please answer for everyone on the invitation.</p>
          )}
        </div>
      </form>
    </div>
  )
}
